import { useState, useCallback } from 'react';

import { useRestApiHeaders } from 'business/contextProviders/useApiHeaders';
import {
  fetchDefectReportV1ControllerGetListByRings,
  fetchRingV1ControllerGetRingsDetails,
} from 'generated/apiComponents';
import { DefectReportEntryDto, RingDetailsDto } from 'generated/apiSchemas';
import { Module_Enum } from 'generated/graphql';

const ALL_DATA_PAGE = 0;
const ALL_DATA_SIZE = 100;

export const useFetchRingData = (constructionSiteId: string) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const [ringData, setRingData] = useState<{
    rings: RingDetailsDto[];
    defects: DefectReportEntryDto[];
  } | null>(null);

  const restHeaders = useRestApiHeaders(Module_Enum.QualityControl);

  const fetchRingData = useCallback(
    async (ringNumber: number) => {
      setIsLoading(true);
      setError(null);
      setRingData(null);

      try {
        const ringResponse = await fetchRingV1ControllerGetRingsDetails({
          pathParams: { constructionSiteId },
          queryParams: { startRing: ringNumber, endRing: ringNumber },
          headers: restHeaders,
        });
        const rings = ringResponse?.rings ?? [];

        const allDefects: DefectReportEntryDto[] = [];
        const response = await fetchDefectReportV1ControllerGetListByRings({
          pathParams: { constructionSiteId },
          queryParams: {
            ringNumberStart: ringNumber,
            ringNumberEnd: ringNumber,
            paginationPage: ALL_DATA_PAGE,
            paginationSize: ALL_DATA_SIZE,
          },
          headers: restHeaders,
        });

        const defects = response?.defectReports ?? [];
        if (defects.length > 0) {
          const typedDefects = defects.map((defect) => ({
            ...defect,
            strokes: defect.strokes as unknown as any[],
            desynchronizedAt: null,
          }));
          allDefects.push(...typedDefects);
        }

        setRingData({
          rings,
          defects: allDefects,
        });
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Technical error'));
      } finally {
        setIsLoading(false);
      }
    },
    [constructionSiteId, restHeaders],
  );

  return {
    fetchRingData,
    ringData,
    isLoading,
    error,
  };
};
